import React, { useState, useRef, useCallback, useEffect } from 'react';
import { LegalSection, Block } from '../../assets/legal/legalContent';

// Pinned to light-mode values — always renders light regardless of system theme.
// Uses explicit hex/rgba from the design token spec; var(--color-*) would flip in dark mode.
// Light palette: surface #FFFFFF, content #1A1A1A, muted #47493F, border rgba(0,0,0,0.10)
const D = {
  bg: '#FFFFFF',
  topBarBg: 'rgba(255, 255, 255, 0.96)',
  title: '#1A1A1A',
  datePill: 'rgba(0, 0, 0, 0.06)',
  dateText: '#47493F',
  divider: 'rgba(0, 0, 0, 0.09)',
  sectionHeader: '#1A1A1A',
  bodyText: '#47493F',
  listDot: '#416451',
  alwaysOpenLabel: '#416451',
  expandToggle: '#47493F',
  chevron: '#AAAAAA',
  progressBar: '#416451',
  contactBg: 'rgba(65, 100, 81, 0.07)',
  contactBorder: 'rgba(65, 100, 81, 0.22)',
  contactEmail: '#416451',
  ackText: '#AAAAAA',
};

interface LegalDocumentProps {
  title: string;
  subtitle: string;
  effectiveDate: string;
  updatedDate?: string;
  sections: LegalSection[];
  onClose: () => void;
  visible: boolean;
}

// Single text block — paragraph or bullet list
const BlockRenderer: React.FC<{ block: Block }> = ({ block }) => {
  if (Array.isArray(block)) {
    return (
      <ul style={{ margin: '2px 0 10px', padding: 0, listStyle: 'none' }}>
        {block.map((item, i) => (
          <li key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
            <span
              style={{
                flexShrink: 0,
                width: '5px',
                height: '5px',
                borderRadius: '50%',
                backgroundColor: D.listDot,
                marginTop: '8px',
                opacity: 0.65,
              }}
            />
            <span style={{ fontSize: '13px', lineHeight: 1.65, color: D.bodyText }}>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  const isAck = block.startsWith('By using the platform');
  const isEmail = block.includes('@ceen.live');

  if (isEmail) {
    const emailMatch = block.match(/[\w.]+@ceen\.live/);
    const email = emailMatch ? emailMatch[0] : null;
    const prefix = email ? block.slice(0, block.indexOf(email)).trim() : null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          backgroundColor: D.contactBg,
          border: `1px solid ${D.contactBorder}`,
          borderRadius: '12px',
          padding: '12px 16px',
          marginTop: '6px',
          marginBottom: '4px',
        }}
      >
        {prefix && (
          <span style={{ fontSize: '12px', color: D.dateText, lineHeight: 1.4 }}>{prefix}</span>
        )}
        <a
          href={`mailto:${email}`}
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: D.contactEmail,
            textDecoration: 'none',
            letterSpacing: '0.01em',
          }}
        >
          {email}
        </a>
      </div>
    );
  }

  return (
    <p
      style={{
        margin: '0 0 10px 0',
        fontSize: isAck ? '11px' : '13px',
        lineHeight: isAck ? 1.5 : 1.7,
        color: isAck ? D.ackText : D.bodyText,
        fontStyle: isAck ? 'italic' : 'normal',
        letterSpacing: isAck ? '0.03em' : 0,
      }}
    >
      {block}
    </p>
  );
};

const AccordionSection: React.FC<{
  section: LegalSection;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ section, isOpen, onToggle }) => {
  const pinned = !!section.alwaysOpen;

  return (
    <div style={{ borderBottom: `1px solid ${D.divider}` }}>
      <button
        onClick={pinned ? undefined : onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0',
          background: 'none',
          border: 'none',
          cursor: pinned ? 'default' : 'pointer',
          textAlign: 'left',
          minHeight: 'unset', // override mobile.css [role="button"] min-height
        }}
        aria-expanded={isOpen}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: pinned ? D.alwaysOpenLabel : D.sectionHeader,
            letterSpacing: '0.02em',
          }}
        >
          {section.title}
        </span>
        {!pinned && (
          <span
            style={{
              color: D.chevron,
              fontSize: '10px',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.22s ease',
              flexShrink: 0,
              marginLeft: '12px',
              display: 'inline-block',
            }}
          >
            ▶
          </span>
        )}
      </button>

      <div
        style={{
          maxHeight: isOpen ? '3000px' : '0',
          overflow: 'hidden',
          transition: isOpen
            ? 'max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'max-height 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div style={{ paddingBottom: '14px' }}>
          {section.content.map((block, i) => (
            <BlockRenderer key={i} block={block} />
          ))}
        </div>
      </div>
    </div>
  );
};

const LegalDocument: React.FC<LegalDocumentProps> = ({
  title,
  subtitle,
  effectiveDate,
  updatedDate,
  sections,
  onClose,
  visible,
}) => {
  const collapsible = sections.filter(s => !s.alwaysOpen);
  const buildInitialOpen = (secs: LegalSection[]) =>
    new Set(secs.filter(s => s.defaultOpen || s.alwaysOpen).map(s => s.id));

  const [openSections, setOpenSections] = useState<Set<string>>(() => buildInitialOpen(sections));
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Reset open state + scroll position when switching between Terms and Privacy
  useEffect(() => {
    setOpenSections(buildInitialOpen(sections));
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (progressBarRef.current) progressBarRef.current.style.width = '0%';
  }, [sections]);

  const allOpen = collapsible.every(s => openSections.has(s.id));

  const toggleSection = useCallback((id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = () => {
    if (allOpen) {
      setOpenSections(new Set(sections.filter(s => s.alwaysOpen).map(s => s.id)));
    } else {
      setOpenSections(new Set(sections.map(s => s.id)));
    }
  };

  // Drive progress bar via DOM ref — avoids re-render on every scroll tick
  const handleScroll = () => {
    const el = scrollRef.current;
    const bar = progressBarRef.current;
    if (!el || !bar) return;
    const pct = el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight);
    const clamped = Math.min(1, Math.max(0, pct));
    bar.style.width = `${clamped * 100}%`;
    setProgress(clamped * 100);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={subtitle}
      aria-hidden={!visible}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        backgroundColor: D.bg,
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
      }}
    >
      {/* Scroll progress bar */}
      <div
        ref={progressBarRef}
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Reading progress"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '2px',
          width: '0%',
          backgroundColor: D.progressBar,
          zIndex: 12,
          pointerEvents: 'none',
        }}
      />

      {/* Top bar */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '16px 24px 14px',
          borderBottom: `1px solid ${D.divider}`,
          backgroundColor: D.topBarBg,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Back to city picker"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 8px 0 0',
            color: D.progressBar,
            fontSize: '18px',
            lineHeight: 1,
            flexShrink: 0,
            minHeight: 'unset',
            minWidth: 'unset',
          }}
        >
          ←
        </button>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: D.title,
            letterSpacing: '0.03em',
          }}
        >
          {subtitle}
        </span>
      </div>

      {/* Scrollable body */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 24px 64px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Doc title block */}
        <div style={{ marginBottom: '20px' }}>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              color: D.title,
              margin: '0 0 10px 0',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span
              style={{
                fontSize: '11px',
                letterSpacing: '0.06em',
                color: D.dateText,
                backgroundColor: D.datePill,
                padding: '3px 10px',
                borderRadius: '999px',
              }}
            >
              Effective {effectiveDate}
            </span>
            {updatedDate && (
              <span
                style={{
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  color: D.dateText,
                  backgroundColor: D.datePill,
                  padding: '3px 10px',
                  borderRadius: '999px',
                }}
              >
                Updated {updatedDate}
              </span>
            )}
            <button
              onClick={toggleAll}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '11px',
                color: D.expandToggle,
                letterSpacing: '0.04em',
                padding: 0,
                textDecoration: 'underline',
                textDecorationColor: 'rgba(0, 0, 0, 0.15)',
                minHeight: 'unset',
                minWidth: 'unset',
              }}
            >
              {allOpen ? 'collapse all' : 'expand all'}
            </button>
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: D.divider, marginBottom: '4px' }} />

        {sections.map(section => (
          <AccordionSection
            key={section.id}
            section={section}
            isOpen={openSections.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default LegalDocument;
